/**
 * /admin/attempts — every attempt, newest first, with the ranked triage panel
 * above it.
 *
 * The ranking lives here rather than on a page of its own because this is
 * already where somebody goes to review attempts: a separate screen would be a
 * screen nobody opens, and the flat reverse-chronological list on its own never
 * says where to start.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, TriangleAlert } from "lucide-react";
import type { AttemptCoverage } from "@exam/shared";
import {
  Badge,
  Card,
  CardHeader,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import { CoverageBar } from "./components/CoverageBar";
import { ReviewTriage } from "./components/ReviewTriage";
import { fetchAttempts, fetchReviewTriage } from "./data/client";
import { formatDateTime, formatDuration, formatMarks, formatPercent } from "./lib/format";
import { useAsync } from "./lib/useAsync";

export function AttemptsPage() {
  const [examId, setExamId] = useState<string>("");

  // Attempts are fetched unfiltered and narrowed in the browser so that the
  // exam picker keeps offering every exam once one is chosen — a filter that
  // deletes its own options is a filter you cannot get back out of. The list is
  // already paged to completion by `fetchAttempts`, so no extra request is made.
  const attempts = useAsync(() => fetchAttempts(), []);

  // The triage call is filtered server-side because it is a ranking, not a
  // list: it is capped, so narrowing it in the browser would silently drop the
  // rows for the chosen exam that fell outside the cap.
  const triage = useAsync(() => fetchReviewTriage({ examId: examId || undefined }), [examId]);

  const rows = attempts.data?.attempts ?? [];

  const exams = useMemo(() => {
    const byId = new Map<string, string>();
    for (const row of rows) byId.set(row.attempt.exam_id, row.exam_title);
    return [...byId].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const examLabel = exams.find(([id]) => id === examId)?.[1] ?? null;

  const visible = examId ? rows.filter((r) => r.attempt.exam_id === examId) : rows;

  // `AdminAttemptSummary` carries no coverage — the attempts endpoint never
  // reads the recording chunks, and widening its contract is not this screen's
  // to do. So coverage is borrowed from the triage response, which computed it
  // anyway. That response is a ranked, capped queue of attempts with something
  // to say about them, so most rows will legitimately have no entry here and
  // render "—": unknown, not "fully recorded".
  const coverageByAttempt = useMemo(() => {
    const map = new Map<string, AttemptCoverage>();
    for (const candidate of triage.data?.candidates ?? []) {
      if (candidate.coverage) map.set(candidate.attempt_id, candidate.coverage);
    }
    return map;
  }, [triage.data]);

  return (
    <div className="space-y-4">
      <PageTitle
        title="Attempts"
        meta="Open one to watch the recording alongside the answers."
        actions={
          <Select
            aria-label="Filter by exam"
            value={examId}
            onChange={(event) => setExamId(event.target.value)}
            className="w-56"
          >
            <option value="">All exams</option>
            {exams.map(([id, title]) => (
              <option key={id} value={id}>
                {title}
              </option>
            ))}
          </Select>
        }
      />

      <FixtureBanner />

      <ReviewTriage state={triage} examLabel={examLabel} />

      <Card>
        <CardHeader
          title="All attempts"
          description="Newest first. Unranked — the panel above is the ranked view."
        />
        {attempts.loading ? (
          <Loading />
        ) : attempts.error ? (
          <div className="p-4">
            <ErrorNote error={attempts.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Exam</TH>
                <TH>Student</TH>
                <TH>Started</TH>
                <TH>Submitted</TH>
                <TH align="right">Took</TH>
                <TH align="right">Score</TH>
                <TH>Recorded</TH>
                <TH align="right">Violations</TH>
                <TH>Status</TH>
                <TH />
              </tr>
            </THead>
            <TBody>
              {visible.length === 0 ? (
                <TableEmpty colSpan={10}>
                  {examId ? "No attempts for this exam yet." : "No attempts yet."}
                </TableEmpty>
              ) : (
                visible.map((row) => {
                  const took =
                    row.attempt.started_at !== null && row.attempt.submitted_at !== null
                      ? row.attempt.submitted_at - row.attempt.started_at
                      : null;
                  // Null score means never graded, not scored zero — and "0%"
                  // beside a dash reads as a mark somebody earned.
                  const ratio =
                    row.attempt.score !== null &&
                    row.attempt.max_score &&
                    row.attempt.max_score > 0
                      ? row.attempt.score / row.attempt.max_score
                      : null;
                  const coverage = coverageByAttempt.get(row.attempt.id) ?? null;

                  return (
                    <TR key={row.attempt.id}>
                      <TD>
                        <Link
                          to={`/admin/attempts/${row.attempt.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {row.exam_title}
                        </Link>
                      </TD>
                      <TD className="text-ink-muted">
                        <Link
                          to={`/admin/students/${row.attempt.user_id}`}
                          className="hover:underline"
                        >
                          {row.student_name}
                        </Link>
                      </TD>
                      <TD className="text-ink-muted">
                        <time>{formatDateTime(row.attempt.started_at)}</time>
                      </TD>
                      <TD className="text-ink-muted">
                        <time>{formatDateTime(row.attempt.submitted_at)}</time>
                      </TD>
                      <TD align="right" className="tabular">
                        {took === null ? "—" : formatDuration(took)}
                      </TD>
                      <TD align="right" className="tabular">
                        {formatMarks(row.attempt.score)} /{" "}
                        {formatMarks(row.attempt.max_score)}
                        {ratio !== null ? (
                          <span className="ml-1.5 text-ink-faint">
                            {formatPercent(ratio)}
                          </span>
                        ) : null}
                      </TD>
                      <TD className="py-1">
                        <CoverageBar coverage={coverage} />
                      </TD>
                      <TD align="right">
                        {row.total_violations > 0 ? (
                          <Badge tone="violation">
                            <TriangleAlert className="size-3" />
                            {row.total_violations}
                          </Badge>
                        ) : (
                          <span className="text-ink-faint">0</span>
                        )}
                      </TD>
                      <TD>
                        <Badge
                          tone={
                            row.attempt.status === "auto_submitted"
                              ? "review"
                              : row.attempt.status === "in_progress"
                                ? "accent"
                                : "neutral"
                          }
                        >
                          {row.attempt.status === "auto_submitted"
                            ? "auto-submitted"
                            : row.attempt.status.replace("_", " ")}
                        </Badge>
                      </TD>
                      <TD align="right">
                        <Link
                          to={`/admin/attempts/${row.attempt.id}`}
                          className="inline-flex text-ink-faint hover:text-ink"
                          aria-label={`Review ${row.exam_title}`}
                        >
                          <ChevronRight className="size-4" />
                        </Link>
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
